/**
 * Display formatting. Intl only; every function accepts API values as-is (ISO strings, nullable numbers) and
 * renders `EMPTY` for missing or invalid input so tables never show "Invalid Date" / "NaN".
 */

export const DEFAULT_LOCALE = 'en-CA';
export const DEFAULT_CURRENCY = 'CAD';
/** Placeholder rendered for missing values. */
export const EMPTY = '—';

type DateInput = string | number | Date | null | undefined;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

type DateFormatOptions = { locale?: string; timeZone?: string };

/** "Sep 23, 2026" */
export function formatDate(value: DateInput, { locale = DEFAULT_LOCALE, timeZone }: DateFormatOptions = {}): string {
  const date = toDate(value);
  return date ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone }).format(date) : EMPTY;
}

/** "Sep 23, 2026, 2:05 p.m." */
export function formatDateTime(value: DateInput, { locale = DEFAULT_LOCALE, timeZone }: DateFormatOptions = {}): string {
  const date = toDate(value);
  return date
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(date)
    : EMPTY;
}

/** Whole calendar days from `now` to `date` in local time (negative = past). DST-safe. */
function calendarDayDiff(date: Date, now: Date): number {
  const a = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((a.getTime() - b.getTime()) / DAY);
}

export type RelativeOptions = {
  locale?: string;
  /**
   * 'time' (default) shows minutes/hours for recent instants; 'day' is for date-only values such as due
   * dates and starts at "today" / "tomorrow" / "yesterday".
   */
  granularity?: 'time' | 'day';
};

/**
 * Human relative time: "just now", "5 minutes ago", "in 2 hours", "today", "yesterday", "3 days ago",
 * "in 2 weeks", "4 months ago", "2 years ago".
 *
 * Thresholds: under 45 s → "just now"; under 45 min → minutes; under 22 h → hours; beyond that calendar-day
 * difference (so "yesterday" always means the previous calendar day, and an instant 22 h+ earlier on the
 * same day reads "today"); 7+ days → weeks; 30+ days → months; 365+ days → years.
 */
export function formatRelative(value: DateInput, now: Date, options: RelativeOptions = {}): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const { locale = DEFAULT_LOCALE, granularity = 'time' } = options;
  const auto = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const always = new Intl.RelativeTimeFormat(locale, { numeric: 'always' });

  if (granularity === 'time') {
    const diffMs = date.getTime() - now.getTime();
    const abs = Math.abs(diffMs);
    if (abs < 45_000) return 'just now';
    if (abs < 45 * MINUTE) return always.format(Math.sign(diffMs) * Math.max(1, Math.round(abs / MINUTE)), 'minute');
    if (abs < 22 * HOUR) return always.format(Math.sign(diffMs) * Math.max(1, Math.round(abs / HOUR)), 'hour');
  }

  const days = calendarDayDiff(date, now);
  const absDays = Math.abs(days);
  if (absDays < 7) return auto.format(days, 'day');
  if (absDays < 30) return always.format(Math.round(days / 7), 'week');
  if (absDays < 365) return always.format(Math.sign(days) * Math.max(1, Math.round(absDays / 30.44)), 'month');
  return always.format(Math.sign(days) * Math.max(1, Math.round(absDays / 365.25)), 'year');
}

export type MoneyOptions = {
  locale?: string;
  /** Compact notation for dense UI: "$1.2M", "$24K". */
  compact?: boolean;
};

/**
 * Currency amount. Whole amounts render without cents ("$24,500"), fractional amounts with two decimals
 * ("$24,500.50"). Foreign currencies are disambiguated by the locale ("US$", "€"). An unknown currency code
 * falls back to "1,234 XYZ" instead of throwing.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string = DEFAULT_CURRENCY,
  { locale = DEFAULT_LOCALE, compact = false }: MoneyOptions = {},
): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return EMPTY;
  const fractionDigits = compact || Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      notation: compact ? 'compact' : 'standard',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: compact ? 1 : fractionDigits,
    }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount)} ${currency}`;
  }
}

/** Phone numbers are normalized server-side (E.164 / national format); display as stored. */
export function formatPhone(value: string | null | undefined): string {
  return value?.trim() || EMPTY;
}

/** Percentage from a 0–100 value (the scale used by stage probabilities and scores): 70 → "70%". */
export function formatPercent(value: number | null | undefined, { locale = DEFAULT_LOCALE, fractionDigits = 0 } = {}): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: fractionDigits,
  }).format(value / 100);
}

/** Plain number with grouping: 12345 → "12,345". */
export function formatNumber(value: number | null | undefined, locale: string = DEFAULT_LOCALE): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return new Intl.NumberFormat(locale).format(value);
}

/** File size in KB below 1 MB, else MB: 2_411_724 → "2.3 MB" (tiny files show "0.1 kB", never "0"). */
export function formatFileSize(bytes: number | null | undefined, locale: string = DEFAULT_LOCALE): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return EMPTY;
  const megabytes = bytes >= 1024 * 1024;
  return new Intl.NumberFormat(locale, { style: 'unit', unit: megabytes ? 'megabyte' : 'kilobyte', maximumFractionDigits: 1 }).format(
    megabytes ? bytes / 1024 / 1024 : Math.max(bytes / 1024, 0.1),
  );
}

/** `pluralize(1, 'lead')` → "1 lead"; `pluralize(3, 'business', 'businesses')` → "3 businesses". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}
